import { CdkColumnDef, CdkTable } from '@angular/cdk/table';
import {
    AfterContentInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';
import { SubscriptionLike } from '../../../models/common';
import { WindowReferenceService } from '../../../services/window-reference/window-reference.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';

@LifecycleAware()
@Component({
    selector: 'lsq-data-table-sticky-header',
    template: `
        <div
            #stickyRef
            [ngStyle]="{
                visibility: this.isHeaderFixedVisible ? 'hidden' : 'visible',
                opacity: this.isHeaderFixedVisible ? 0 : 1
            }"
            class="sticky-table-header stick-to-top"
        >
            <table
                #stickyTable
                cdk-table
                class="table"
                [ngClass]="{ loading: dataSource.loading$ | async }"
            >
                <tr cdk-header-row *cdkHeaderRowDef="columnNames"></tr>
            </table>
        </div>
    `
})
export class LsqDataTableStickyHeaderComponent implements AfterContentInit, OnDestroy {
    @ViewChild('stickyRef', { read: ElementRef }) public stickyRef: ElementRef;
    @ViewChild('stickyTable', { static: true }) public stickyTable: CdkTable<any>;
    @Input() public getTableHeaderRef: () => ElementRef;
    @Input() public columnDefs: CdkColumnDef[];
    @Input() public dataSource: LsqListSource<any>;
    @Input() public columnNames: string[] = [];
    @Output() public stickyChange = new EventEmitter<boolean>();

    protected windowScrollSubscription: SubscriptionLike;
    public isHeaderFixedVisible = true;

    constructor(private windowReferenceService: WindowReferenceService, private ngZone: NgZone) {}

    // Lifecycle methods.
    public ngAfterContentInit(): void {
        if (this.stickyTable) {
            this.columnDefs.forEach((columnDef) => {
                this.stickyTable.addColumnDef(columnDef);
            });
        }

        this.windowReferenceService.resizeStream.pipe(forLifeOf(this)).subscribe(() => {
            this.updateHeaderVisibility();
        });

        this.windowScrollSubscription = this.windowReferenceService
            .createScrollStream()
            .subscribe(() => {
                this.updateHeaderVisibility();
            });

        this.updateHeaderVisibility();
    }

    public ngOnDestroy(): void {
        if (this.windowScrollSubscription) {
            this.windowScrollSubscription.unsubscribe();
        }
    }

    // Event handlers/async methods.
    private updateHeaderVisibility(): void {
        const tableHeaderRefRow =
            this.getTableHeaderRef() && this.getTableHeaderRef().nativeElement;
        const stickyRefRow = this.stickyRef && this.stickyRef.nativeElement.querySelector('tr');

        if (tableHeaderRefRow && stickyRefRow) {
            const tableHeaderTop = tableHeaderRefRow.getBoundingClientRect().top;
            const stickyHeaderTop = stickyRefRow.getBoundingClientRect().top;

            if (tableHeaderTop < stickyHeaderTop) {
                this.updateStickyHeader();
                if (this.isHeaderFixedVisible) {
                    this.ngZone.run(() => {
                        this.isHeaderFixedVisible = false;
                        this.stickyChange.emit(this.isHeaderStuck());
                    });
                }
            } else {
                if (!this.isHeaderFixedVisible) {
                    this.ngZone.run(() => {
                        this.isHeaderFixedVisible = true;
                        this.stickyChange.emit(this.isHeaderStuck());
                    });
                }
            }
        }
    }

    private updateStickyHeader(): void {
        if (this.getTableHeaderRef() && this.stickyRef) {
            const headers = Array.from(
                this.getTableHeaderRef().nativeElement.querySelectorAll('th')
            ) as HTMLElement[];

            const stickyHeaders = Array.from(
                this.stickyRef.nativeElement.querySelectorAll('th')
            ) as HTMLElement[];

            this.stickyRef.nativeElement.querySelector('table').style.width =
                this.getTableHeaderRef().nativeElement.offsetWidth + 'px';
            this.stickyRef.nativeElement.style.left =
                this.getTableHeaderRef().nativeElement.getBoundingClientRect().left + 'px';

            headers.forEach((header, index) => {
                stickyHeaders[index].style.width = header.offsetWidth + 'px';
            });
        }
    }

    // Boolean methods.
    public isHeaderStuck(): boolean {
        return !this.isHeaderFixedVisible;
    }
}
